// frontend/src/lib/identity.js
// ✅ UPDATED: Pure localStorage reader — Pi.authenticate is ONLY called in App.js
// Adds: isPiAuthenticated() + getStoredAuth() helpers for callers that need to
// gate UI/actions on a real Pi session.

const STORAGE_KEY = "artibytes_auth_v1";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== "undefined" && window.__ARTIBYTES_API_BASE__
    ? String(window.__ARTIBYTES_API_BASE__)
    : "") ||
  "https://artibytes-api-testnet-191042099955.europe-west4.run.app";

/** Detect Pi SDK (Pi Browser only) */
export function hasPiSdk() {
  return typeof window !== "undefined" && !!window.Pi && !!window.Pi.authenticate;
}

/** Read the stored auth blob (or null if nothing/invalid). */
export function getStoredAuth() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Returns true if there's a usable Pi session in localStorage.
 * Use this to gate payment buttons, A2U claims, etc.
 */
export function isPiAuthenticated() {
  const stored = getStoredAuth();
  return !!(stored?.accessToken && stored?.user?.uid);
}

/**
 * Sign in helper — reads from localStorage only and verifies with backend.
 * App.js is responsible for calling Pi.authenticate and storing the token.
 */
export async function signInWithPi() {
  if (!hasPiSdk()) {
    throw new Error("Pi SDK not available (open in Pi Browser)");
  }

  const stored = getStoredAuth();
  if (!stored?.accessToken) {
    throw new Error("Not authenticated — token not found in storage");
  }

  // Hit /api/me so backend verifies token via /v2/me and upserts user.
  // Don't swallow — caller may want to react to a 401.
  const res = await fetch(`${API_BASE}/api/me`, {
    method: "GET",
    credentials: "omit",
    headers: getIdentityHeaders(),
  });

  if (!res.ok) {
    throw new Error(`Backend rejected token (HTTP ${res.status})`);
  }

  return stored.user;
}

/** Logout helper */
export async function signOutPi() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem("artibytes_access_token");
    localStorage.removeItem("mobile-app-webview-ui_access-token");
  } catch {}
}

/**
 * No-op — token refresh is handled by App.js (which re-runs Pi.authenticate
 * on app reload or via the retry button on AuthGate).
 */
export async function refreshPiToken() {
  console.log("[Identity] Token refresh is handled by App.js");
  return false;
}

/** Build headers for API calls. */
export function getIdentityHeaders() {
  const h = {};
  const stored = getStoredAuth();
  if (stored?.accessToken) {
    h["Authorization"] = `Bearer ${stored.accessToken}`;
    h["x-pi-access-token"] = stored.accessToken;
  }
  if (stored?.user?.uid) h["x-pi-uid"] = stored.user.uid;
  if (stored?.user?.username) h["x-pi-username"] = stored.user.username;
  return h;
}

/** Back-compat shim — returns stored identity without triggering auth. */
export async function waitForIdentity() {
  const stored = getStoredAuth();
  return { headers: getIdentityHeaders(), user: stored?.user || null };
}

export function initializeAuth() {
  // No-op — auth is handled by App.js
}